<template>
    <div :class="`captcha-align-${localAlign} captcha`">
        <label for="captcha" class="field-label" @click="focusFieldLabel" v-if="!editingFieldLabel && showLabel">
            {{ displayFieldLabel }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        <input
            v-if="editingFieldLabel"
            type="text"
            class="field-label-input"
            placeholder="Güvenlik Doğrulaması"
            v-model="localFieldLabel"
            @blur="stopEditingFieldLabel"
            @keydown.enter="stopEditingFieldLabel"
            ref="fieldLabelInput"
        />

        <div class="captcha-container">
            <div class="captcha-preview">
                <canvas
                    ref="captchaCanvas"
                    :width="captchaWidth"
                    :height="captchaHeight"
                    class="captcha-canvas"
                ></canvas>
            </div>

            <div class="captcha-input-container">
                <input
                    type="text"
                    v-model="captchaInput"
                    :placeholder="placeholder || 'Yukarıdaki kodu girin'"
                    class="captcha-input"
                    readonly
                />
                <button @click="refreshCaptcha" class="refresh-icon-button" type="button">
                    <md-icon>refresh</md-icon>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "FormCaptcha",
    props: {
        fieldLabel: String,
        align: {
            type: String,
            default: 'center'
        },
        placeholder: {
            type: String,
            default: 'Yukarıdaki kodu girin'
        },
        isRequired: {
            type: Boolean,
            default: true
        },
        item: Object,
    },
    data() {
        return {
            localFieldLabel: this.fieldLabel || "Güvenlik Doğrulaması",
            localAlign: this.align || "center",
            editingFieldLabel: false,
            captchaInput: '',
            currentCaptchaCode: '',
            captchaWidth: 180,
            captchaHeight: 60,
        };
    },
    computed: {
        displayFieldLabel() {
            return this.localFieldLabel || "Güvenlik Doğrulaması";
        },
        showLabel() {
            return true;
        },
    },
    mounted() {
        this.generateCaptcha();
        console.log('🔴 Captcha mounted:', {
            fieldLabel: this.fieldLabel,
            placeholder: this.placeholder
        });
    },
    methods: {
        ...mapActions({
            updateFormField: "forms/updateFormField",
        }),

        focusFieldLabel() {
            this.editingFieldLabel = true;
            this.$nextTick(() => {
                if (this.$refs.fieldLabelInput) {
                    this.$refs.fieldLabelInput.focus();
                }
            });
        },

        stopEditingFieldLabel() {
            this.editingFieldLabel = false;
            this.updateField();
        },

        generateCaptcha() {
            const canvas = this.$refs.captchaCanvas;
            if (!canvas) return;

            const ctx = canvas.getContext('2d');

            ctx.clearRect(0, 0, this.captchaWidth, this.captchaHeight);
            ctx.fillStyle = this.backgroundColor;
            ctx.fillRect(0, 0, this.captchaWidth, this.captchaHeight);

            this.currentCaptchaCode = this.generateCode();

            this.addNoise(ctx);

            this.drawCaptchaText(ctx, this.currentCaptchaCode);

            console.log('🔴 Generated captcha:', this.currentCaptchaCode);
        },

        generateCode() {
            const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Karışık karakterler çıkarıldı
            let code = '';

            for (let i = 0; i < 5; i++) {
                const randomIndex = Math.floor(Math.random() * characters.length);
                code += characters[randomIndex];
            }

            return code;
        },

        addNoise(ctx) {
            for (let i = 0; i < 80; i++) {
                ctx.fillStyle = `rgba(${Math.random() * 100}, ${Math.random() * 100}, ${Math.random() * 100}, 0.4)`;
                ctx.fillRect(Math.random() * this.captchaWidth, Math.random() * this.captchaHeight, 2, 2);
            }

            for (let i = 0; i < 3; i++) {
                ctx.strokeStyle = `rgba(${Math.random() * 150}, ${Math.random() * 150}, ${Math.random() * 150}, 0.6)`;
                ctx.lineWidth = Math.random() * 2 + 1;
                ctx.beginPath();
                ctx.moveTo(Math.random() * this.captchaWidth, Math.random() * this.captchaHeight);
                ctx.lineTo(Math.random() * this.captchaWidth, Math.random() * this.captchaHeight);
                ctx.stroke();
            }
        },

        drawCaptchaText(ctx, code) {
            const fontSize = 28;
            ctx.font = `bold ${fontSize}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const spacing = this.captchaWidth / (code.length + 1);

            for (let i = 0; i < code.length; i++) {
                const char = code[i];
                const x = spacing * (i + 1) + (Math.random() - 0.5) * 8; // Hafif random pozisyon
                const y = this.captchaHeight / 2 + (Math.random() - 0.5) * 8;

                const rotation = (Math.random() - 0.5) * 0.3;
                ctx.save();
                ctx.translate(x, y);
                ctx.rotate(rotation);

                ctx.fillStyle = '#333333';
                ctx.fillText(char, 0, 0);

                ctx.restore();
            }
        },

        refreshCaptcha() {
            this.generateCaptcha();
            this.captchaInput = '';
        },

        updateField() {
            const fieldData = {
                fieldLabel: this.localFieldLabel,
                align: this.localAlign,
            };

            this.$emit("update-field", fieldData);
            console.log('🔴 Captcha field updated:', fieldData);
        },
    },
    watch: {
        fieldLabel(newVal) {
            this.localFieldLabel = newVal || "Güvenlik Doğrulaması";
        },
        align(newVal) {
            this.localAlign = newVal || "center";
        },
    }
};
</script>

<style lang="scss" scoped>
.captcha {
    width: 100%;
    max-width: 400px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.75rem, 1.8vw, 1rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;


    .field-label {
        display: block;
        font-size: clamp(0.875rem, 1.8vw, 1rem);
        font-weight: 600;
        color: rgba(55, 65, 81, 0.9);
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        cursor: pointer;
        transition: color 0.2s ease;

        &:hover {
            color: #f26a52;
        }

        .required-indicator {
            color: #ef4444;
            margin-left: 0.25rem;
            font-weight: 700;
        }
    }

    .field-label-input {
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        font-size: clamp(0.875rem, 1.8vw, 1rem);
        font-weight: 600;
        color: rgba(55, 65, 81, 0.9);
        background: rgba(255, 255, 255, 0.8);
        border: 2px solid #f26a52;
        border-radius: 0.5rem;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        transition: all 0.2s ease;

        &:focus {
            outline: none;
            background: rgba(255, 255, 255, 0.95);
            box-shadow: 0 0 0 3px rgba(242, 106, 82, 0.1);
        }
    }

    .captcha-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: clamp(0.75rem, 1.5vw, 1rem);
    }

    .captcha-preview {
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        border: 1px solid #e5e7eb;
        background: #f9fafb;

        .captcha-canvas {
            display: block;
            border-radius: 0.375rem 0.375rem 0 0;
        }

    }

    .captcha-input-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;

        .captcha-input {
            width: 140px;
            padding: 0.75rem;
            font-size: 1rem;
            font-weight: 600;
            text-align: center;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: #374151;
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 0.5rem;
            transition: all 0.3s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

            &:focus {
                outline: none;
                border-color: #f26a52;
                box-shadow: 0 0 0 3px rgba(242, 106, 82, 0.1);
            }

            &:hover {
                border-color: rgba(242, 106, 82, 0.4);
            }

            &::placeholder {
                color: rgba(107, 114, 128, 0.7);
                font-weight: 500;
                letter-spacing: normal;
                text-transform: none;
            }
        }

        .refresh-icon-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2.75rem;
            height: 2.75rem;
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 0.5rem;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

            &:hover {
                border-color: #f26a52;
                box-shadow: 0 2px 6px rgba(242, 106, 82, 0.15);
            }

            .md-icon {
                font-size: 1.125rem;
                color: #6b7280;
                transition: transform 0.3s ease, color 0.3s ease;
            }

            &:hover .md-icon {
                transform: rotate(180deg);
                color: #f26a52;
            }
        }
    }

    .captcha-info {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 1rem;
        background: rgba(248, 250, 252, 0.8);
        border-radius: 0.5rem;
        border: 1px solid rgba(226, 232, 240, 0.8);

        .info-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 0.875rem;

            .info-label {
                color: rgba(107, 114, 128, 0.8);
                font-weight: 500;
            }

            .info-value {
                color: rgba(55, 65, 81, 0.9);
                font-weight: 600;
                text-transform: capitalize;
            }
        }

        .config-warning {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem;
            background: #fef3cd;
            border: 1px solid #fde047;
            border-radius: 0.375rem;
            color: #a16207;
            font-size: 0.875rem;
            font-weight: 500;

            .warning-icon {
                font-size: 1.125rem;
                color: #d97706;
            }
        }
    }
}

.captcha-align-left {
    .field-label {
        text-align: left;
    }

    .captcha-container {
        align-items: flex-start;
    }
}

.captcha-align-center {
    .field-label {
        text-align: center;
    }

    .captcha-container {
        align-items: center;
    }
}

.captcha-align-right {
    .field-label {
        text-align: right;
    }

    .captcha-container {
        align-items: flex-end;
    }
}

@media (max-width: 768px) {
    .captcha {
        margin: 0.5rem auto;
        padding: 0.75rem;

        .recaptcha-box,
        .hcaptcha-box,
        .turnstile-box {
            min-width: 240px;
            padding: 0.75rem;
        }

        .captcha-info {
            padding: 0.75rem;

            .info-item {
                font-size: 0.8rem;
            }
        }
    }
}

@media (max-width: 480px) {
    .captcha {
        margin: 0.25rem auto;
        padding: 0.5rem;

        .recaptcha-box,
        .hcaptcha-box,
        .turnstile-box {
            min-width: 200px;
            padding: 0.5rem;
            flex-direction: column;
            text-align: center;
            gap: 0.5rem;
        }
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.captcha {
    will-change: transform;
}

.captcha-preview {
    will-change: transform, box-shadow;
}

@media (prefers-reduced-motion: reduce) {
    .captcha {
        transition: none;

        &:hover {
            transform: none;
        }
    }

    .captcha-preview {
        transition: none;

        &:hover {
            transform: none;
        }
    }

    .turnstile-spinner {
        animation: none;
    }
}
</style>
